<template>
  <div class="raise-detail-panel" v-loading="detailLoading">
    <!-- 基本信息区域 -->
    <div class="detail-section">
      <div class="info-row">
        <span class="info-label">任务状态</span>
        <span class="info-value">
          <span class="status-dot" :class="{ active: detailData.raiseStatus === 1 }"></span>
          {{ detailData.raiseStatusName || '--' }}
          <el-button v-if="detailData.canStop === 1" type="text" style="padding: 0" @click="handleStop"> 停止 </el-button>
          <span v-else> 当前任务已停止, 停止后不可恢复, 可返回重新创建起量任务</span>
        </span>
      </div>
    </div>

    <!-- 预算信息区域 -->
    <div class="detail-section">
      <div class="info-row">
        <span class="info-label">起量预算(元)</span>
        <span class="info-value">{{ formatBudget(detailData.budget?.raiseBudget) }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">起量预算余额(元)</span>
        <span class="info-value">{{ formatBudget(detailData.budget?.remainBudget) }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">已消耗(元)</span>
        <span class="info-value">{{ formatBudget(detailData.budget?.usedBudget) }}</span>
      </div>
    </div>

    <!-- 时间信息区域 -->
    <div class="detail-section">
      <div class="info-row">
        <span class="info-label">生效时间</span>
        <span class="info-value">{{ detailData.timeRange?.startTime || '--' }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">结束时间</span>
        <span class="info-value">{{ detailData.timeRange?.endTime || '--' }}</span>
      </div>
    </div>

    <!-- 素材信息区域 -->
    <div class="detail-section material-section">
      <div class="section-header">
        <span class="info-label">起量素材</span>
        <span class="material-count">共 {{ detailData.materials?.length || 0 }} 个</span>
      </div>
      <div v-if="detailData.materials && detailData.materials.length > 0" class="material-list">
        <div v-for="material in detailData.materials" :key="material.materialId" class="material-item">
          <div class="material-thumb">
            <el-image
              v-if="material.materialType === 'VIDEO'"
              v-popover="{ url: material.url, type: 'video', height: 220 }"
              style="height: 60px; cursor: pointer"
              :src="material.coverUrl"
            ></el-image>

            <el-image
              v-if="material.materialType === 'IMAGE'"
              v-popover="{ url: material.url, type: 'image', height: 220 }"
              style="height: 60px; cursor: pointer"
              :src="material.url"
            ></el-image>
          </div>
          <div class="material-info">
            <p class="material-name">素材名称：{{ material.name }}</p>
            <p class="material-id">ID：{{ material.materialId }}</p>
          </div>
        </div>
      </div>
      <div v-else class="empty-material">
        <span>--</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps } from 'vue'
import { MessageBox } from 'element-ui'
import { Message } from 'view-design'
import { getPtomPlanToutiaoRaiseDetail, ptomPlanToutiaoRaiseTaskStop } from '@/api/ptom.data.model'

const props = defineProps({
  planId: {
    type: Number
  },
  recordId: {
    type: String
  }
})

const detailData = ref({})
const detailLoading = ref(false)

const getRaiseTaskDetail = () => {
  detailLoading.value = true
  getPtomPlanToutiaoRaiseDetail({ planId: props.planId, recordId: props.recordId })
    .then((data) => {
      if (data) {
        detailData.value = data
      }
    })
    .finally(() => {
      detailLoading.value = false
    })
}

getRaiseTaskDetail()

// 格式化预算金额
const formatBudget = (value) => {
  const num = Number(value || 0)
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// 停止起量任务
const handleStop = () => {
  MessageBox.alert('停止后，任务无法再次恢复执行，请谨慎操作', '确定要停止起量吗？', {
    type: 'warning',
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    callback: (action) => {
      if (action === 'confirm') {
        ptomPlanToutiaoRaiseTaskStop({ planId: props.planId, recordId: props.recordId }).then((data) => {
          if (data) {
            Message.success('停止成功')
            getRaiseTaskDetail()
          }
        })
      }
    }
  })
}
</script>

<style scoped lang="scss">
.raise-detail-panel {
  width: 100%;
  padding: 20px;
}

.detail-section {
  margin-bottom: 24px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &:last-child {
    margin-bottom: 0;
  }
}

.info-row {
  display: flex;
  align-items: center;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
}

.info-label {
  flex-shrink: 0;
  width: 160px;
  color: #999;
  font-size: 14px;
}

.info-value {
  flex: 1;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ccc;
  margin-right: 6px;

  &.active {
    background: #52c41a;
    box-shadow: 0 0 4px rgba(82, 196, 26, 0.5);
  }
}

.material-section {
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .material-count {
    color: #999;
    font-size: 13px;
  }
}

.material-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.material-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 6px;
  transition: all 0.3s;

  &:hover {
    background: #e8ecf1;
  }
}

.material-thumb {
  position: relative;
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #ddd;
}

.material-info {
  margin-left: 12px;
  flex: 1;
  min-width: 0;

  .material-name {
    margin: 0 0 6px;
    color: #333;
    font-size: 13px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .material-id {
    margin: 0;
    color: #999;
    font-size: 12px;
  }
}

.empty-material {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
  background: #f5f7fa;
  border-radius: 6px;
}

@media (max-width: 768px) {
  .raise-detail-panel {
    padding: 12px;
  }

  .detail-section {
    padding: 12px 16px;
  }

  .info-label {
    width: 120px;
    font-size: 13px;
  }

  .info-value {
    font-size: 13px;
  }

  .material-list {
    grid-template-columns: 1fr;
  }
}
</style>
